import React from 'react';
import SettingsDropdown, {
  SettingsDropdownData
} from '../../UI/settings-dropdown';
import SvgIcon from '../../SVG/svg';
import { Radio, MessageSquare, Trash2, Pencil } from 'lucide-react';
import { CommentMetadata } from '../../../Models/trips/trip-activity.model';

interface ItemCardHeaderProps {
  id: string;
  title: string;
  subtitle?: string;

  // Selection
  selectable?: boolean;
  isSelected?: boolean;
  onSelectionChange?: (checked: boolean) => void;

  // Icon
  icon?: React.ReactNode;
  iconColor?: string;

  // Source selection for distance calculations
  isSource?: boolean;
  onSourceChange?: (activityId: string | null) => void;

  // Actions
  optionsMenu?: SettingsDropdownData[];
  onMessageClick?: (id: string) => void;
  onDeleteClick?: (id: string) => void;
  onEditClick?: (id: string) => void;
  onAddClick?: (id: string) => void;

  // Comment metadata
  commentMetadata?: CommentMetadata;

  // Allow string ID for component usage
  stringId?: boolean;

  // State
  isDragging?: boolean;
  mapped?: boolean;
  unmappedLabel?: string;
  addButtonLoading?: boolean;
  addButtonDisabled?: boolean;

  // Header Item Visibility
  showCheckbox?: boolean;
  showMapIcon?: boolean;
  showSourceButton?: boolean;
  showMessageButton?: boolean;
  showDeleteButton?: boolean;
  showEditButton?: boolean;
  showAddButton?: boolean;
  showOptionsMenu?: boolean;
}

export const ItemCardHeader: React.FC<ItemCardHeaderProps> = ({
  id,
  title,
  subtitle,
  selectable,
  isSelected,
  onSelectionChange,
  icon,
  iconColor,
  isSource,
  onSourceChange,
  optionsMenu,
  onMessageClick,
  onDeleteClick,
  onEditClick,
  onAddClick,
  commentMetadata,
  isDragging,
  mapped = true,
  unmappedLabel = '(not mapped)',
  addButtonLoading = false,
  addButtonDisabled = false,
  showCheckbox,
  showMapIcon = true,
  showSourceButton = true,
  showMessageButton = false,
  showDeleteButton = false,
  showEditButton = false,
  showAddButton = false,
  showOptionsMenu = true
}) => {
  console.log('[ItemCardHeader] RENDERED - title:', title);

  // showCheckbox defaults to selectable if not explicitly set
  const shouldShowCheckbox =
    showCheckbox !== undefined ? showCheckbox : selectable;

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr auto',
          alignItems: 'center',
          gap: '8px',
          width: '100%'
          // padding: '5px'
        }}
      >
        {/* Left section: Checkbox and Icon */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            justifyContent: 'flex-start'
          }}
        >
          {selectable && shouldShowCheckbox && (
            <input
              type='checkbox'
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelectionChange?.(e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                width: '16px',
                height: '16px',
                cursor: 'pointer',
                accentColor: '#842234',
                flexShrink: 0
              }}
              title={isSelected ? 'Hide from map' : 'Show on map'}
            />
          )}
          {icon && showMapIcon && (
            <div style={{ cursor: 'move' }}>
              <div
                style={{
                  display: 'inline-block',
                  border: '2px solid transparent',
                  borderRadius: '4px',
                  padding: '2px',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                {icon}
              </div>
            </div>
          )}
        </div>

        {/* Center section: Title and Source Selector */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            paddingLeft: '8px',
            paddingRight: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <span
            style={{
              flex: 1,
              fontSize: '16px',
              fontWeight: 'bold',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            title={title}
          >
            {title}
          </span>
          {!mapped && (
            <span
              style={{
                fontSize: '12px',
                color: '#6c757d',
                fontStyle: 'italic'
              }}
              title='This item cannot be viewed on the map. It has no lat/lng values.'
            >
              {unmappedLabel}
            </span>
          )}
          {/* Source Selection Button */}
          {onSourceChange && showSourceButton && isSource && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSourceChange(null);
              }}
              style={{
                width: '20px',
                height: '20px',
                border: 'none',
                background: 'transparent',
                color: '#a15f6c',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0
              }}
              title='Remove as source'
            >
              <Radio size={16} />
            </button>
          )}
          {onSourceChange && showSourceButton && !isSource && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onSourceChange(id);
              }}
              style={{
                width: '20px',
                height: '20px',
                border: 'none',
                background: 'transparent',
                color: '#b4bcc7',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0
              }}
              title='Set as source for distance calculations'
            >
              <Radio size={16} />
            </button>
          )}
        </div>

        {/* Right section: Actions */}
        <div
          style={{
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          {/* Message Button */}
          {showMessageButton && onMessageClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onMessageClick(id);
              }}
              style={{
                width: '24px',
                height: '24px',
                border: 'none',
                background: 'transparent',
                color: '#7a8696',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                position: 'relative'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={
                commentMetadata && commentMetadata.count > 0
                  ? `${commentMetadata.count} comment${commentMetadata.count === 1 ? '' : 's'}${
                      commentMetadata.hasRecentActivity ? ' (new)' : ''
                    }`
                  : 'Add comment'
              }
            >
              <MessageSquare size={16} />
              {commentMetadata && commentMetadata.count > 0 && (
                <span
                  style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    backgroundColor: commentMetadata.hasRecentActivity
                      ? '#dc3545'
                      : '#6c757d',
                    color: 'white',
                    borderRadius: '10px',
                    fontSize: '9px',
                    fontWeight: 'bold',
                    minWidth: '16px',
                    height: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '0 4px',
                    lineHeight: 1,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }}
                >
                  {commentMetadata.count > 99 ? '99+' : commentMetadata.count}
                </span>
              )}
            </button>
          )}

          {/* Edit Button */}
          {showEditButton && onEditClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onEditClick(id);
              }}
              style={{
                width: '24px',
                height: '24px',
                border: 'none',
                background: 'transparent',
                color: '#7a8696',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title='Edit'
            >
              <Pencil size={16} />
            </button>
          )}

          {/* Delete Button */}
          {showDeleteButton && onDeleteClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onDeleteClick(id);
              }}
              style={{
                width: '24px',
                height: '24px',
                border: 'none',
                background: 'transparent',
                color: '#e17b88',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                borderRadius: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#ffe0e0';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title='Delete'
            >
              <Trash2 size={16} />
            </button>
          )}

          {/* Add Button */}
          {showAddButton && onAddClick && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!addButtonDisabled) {
                  onAddClick(id);
                }
              }}
              disabled={addButtonDisabled || addButtonLoading}
              style={{
                width: '24px',
                height: '24px',
                border: 'none',
                background: 'transparent',
                color:
                  addButtonDisabled || addButtonLoading ? '#b4bcc7' : '#a15f6c',
                cursor:
                  addButtonDisabled || addButtonLoading
                    ? 'not-allowed'
                    : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                padding: 0,
                borderRadius: '4px',
                transition: 'background-color 0.2s',
                fontSize: '18px',
                fontWeight: 'bold',
                opacity: addButtonDisabled || addButtonLoading ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!addButtonDisabled && !addButtonLoading) {
                  e.currentTarget.style.backgroundColor = '#f0e0e5';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              title={addButtonLoading ? 'Adding...' : 'Add to trip'}
            >
              {addButtonLoading ? (
                <div
                  style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid #999',
                    borderTopColor: 'transparent',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }}
                />
              ) : (
                '+'
              )}
            </button>
          )}

          {/* Options Menu */}
          {showOptionsMenu && optionsMenu && optionsMenu.length > 0 && (
            <SettingsDropdown
              data={optionsMenu}
              id={id as any}
              icon={
                <SvgIcon
                  type='dots-vertical'
                  height={15}
                  fill='#7a8696'
                />
              }
              bodyClassName='board-dropdown'
            />
          )}
        </div>
      </div>
    </>
  );
};
